import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { apiRequest } from "util/services";
import firebase from "../../FirebaseConfig";
import "../../stylesheets/EmailVerification.css";
import Signup from "../Signup";
import { LOGGED } from "../../util/constants";
import { buildLoginRedirectPath } from "util/routeAccess";
import { resolvePostEmailVerificationPath } from "../Signup/signupProducts";

const readOobCode = (propCode) => {
  if (propCode) return propCode;
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("oobCode");
};

const EmailVerification = ({
  user,
  history,
  continueUrl,
  authenticated,
  oobCode: oobCodeProp,
}) => {
  const { t } = useTranslation();
  const [emailVerified, setEmailVerified] = useState(false);
  const [hasCheckedVerification, setHasCheckedVerification] = useState(false);
  const userUid = user?.uid;
  const userPlan = user?.plan;

  const goAfterVerified = (hasPlan) => {
    const next = resolvePostEmailVerificationPath({
      continueUrl,
      hasPlan: Boolean(hasPlan),
      intendedProduct: user?.intendedProduct,
    });
    history.replace(next);
  };

  useEffect(() => {
    if (hasCheckedVerification) return;
    if (!user?.uid) {
      if (authenticated === LOGGED) return;
      const oobCode = readOobCode(oobCodeProp);
      if (oobCode) {
        setHasCheckedVerification(true);
        firebase
          .auth()
          .applyActionCode(oobCode)
          .then(() => {
            notification.success({
              message: t("auth.successful"),
              description: "Email verified successfully. Please log in.",
            });
            history.replace(
              buildLoginRedirectPath(
                resolvePostEmailVerificationPath({ continueUrl })
              )
            );
          })
          .catch((error) => {
            notification.error({
              message: t("auth.error"),
              description:
                error?.message || "Verification link is invalid or expired",
            });
            history.replace("/login");
          });
        return;
      }
      notification.error({
        message: t("auth.error"),
        description: "You will be directed to pivony home page",
      });
      history.replace("/");
      return;
    }

    setHasCheckedVerification(true);
    const oobCode = readOobCode(oobCodeProp);

    const redirectAfterVerified = () => {
      firebase.database().ref(`/users/${user.uid}/emailVerified`).set(true);
      setEmailVerified(true);
      notification.success({
        message: t("auth.successful"),
        description: "Email verified successfully",
      });
      goAfterVerified(user?.plan !== undefined);
    };

    const syncVerified = async () => {
      const result = await apiRequest(
        "users/emailVerification",
        "POST",
        { user_id: user.uid },
        true
      );
      if (result?.error) {
        firebase.database().ref(`/users/${user.uid}/emailVerified`).set(true);
      }
      redirectAfterVerified();
    };

    (async () => {
      try {
        if (oobCode) {
          await firebase.auth().applyActionCode(oobCode);
        }
        await firebase.auth().currentUser?.reload();
        if (!firebase.auth().currentUser?.emailVerified) {
          if (oobCode) {
            notification.error({
              message: t("auth.error"),
              description: "Email could not be verified",
            });
          }
          return;
        }
        await syncVerified();
      } catch (error) {
        try {
          await firebase.auth().currentUser?.reload();
        } catch {
          // ignore
        }
        if (firebase.auth().currentUser?.emailVerified) {
          await syncVerified();
          return;
        }
        notification.error({
          message: t("auth.error"),
          description:
            error?.message || "Verification link is invalid or expired",
        });
      }
    })();
  }, [
    userUid,
    userPlan,
    history,
    continueUrl,
    authenticated,
    hasCheckedVerification,
    oobCodeProp,
    t,
    user,
  ]);

  if (emailVerified) {
    return (
      <div className="EmailVerification">
        <h1>Email verified</h1>
        <p>Continuing to the next step…</p>
      </div>
    );
  }

  return <Signup user={user} authenticated={authenticated} />;
};

export default EmailVerification;
